/**
 * Provider-accurate text length counting shared by the server adapters'
 * validatePost and the composer's character ring, so the UI always matches
 * what publish will enforce.
 *
 * - Bluesky counts grapheme clusters (lexicon `maxGraphemes: 300`) with a
 *   3,000-byte UTF-8 ceiling (`maxLength`).
 * - Mastodon counts every URL as 23 characters and ignores the domain part of
 *   remote mentions (`@user@instance` counts as `@user`).
 */

import { graphemeSegmenter } from "./grapheme-segmenter";
import { weightedTweetLength } from "./x-weighted-length";

/** Number of user-perceived characters (grapheme clusters). */
export function graphemeLength(text: string): number {
  let count = 0;
  for (const _segment of graphemeSegmenter().segment(text)) {
    count += 1;
  }
  return count;
}

const utf8Encoder = new TextEncoder();

/** UTF-8 byte length — Bluesky's `maxLength` and YouTube descriptions use bytes. */
export function utf8ByteLength(text: string): number {
  return utf8Encoder.encode(text).length;
}

const MASTODON_URL_WEIGHT = 23;
const mastodonUrlPattern = /https?:\/\/\S+/gi;
/**
 * Remote mention: `@user@instance.social`. Mastodon counts only `@user`;
 * the leading char group keeps emails (`a@b@c` can't occur) and paths intact.
 */
const mastodonMentionPattern =
  /(^|[^/\w])@([a-z0-9_]+(?:[a-z0-9_.-]*[a-z0-9_]+)?)@[\w.-]+/gi;

/**
 * Mastodon's weighted status length: URLs count as 23 regardless of length,
 * remote mention domains are free. The remainder is counted as code points
 * (an upper bound on Mastodon's grapheme counting, so we never allow text an
 * instance would reject).
 */
export function mastodonWeightedLength(text: string): number {
  let weight = 0;
  const withoutUrls = text.replace(mastodonUrlPattern, () => {
    weight += MASTODON_URL_WEIGHT;
    return "";
  });
  const withoutMentionDomains = withoutUrls.replace(
    mastodonMentionPattern,
    (_match, lead: string, user: string) => `${lead}@${user}`,
  );
  return weight + Array.from(withoutMentionDomains).length;
}

/**
 * A post's length the way `provider` counts it against `maxTextLength`: X
 * weights URLs, CJK and emoji; Bluesky counts graphemes; Mastodon weighs URLs
 * as 23; YouTube's description limit is UTF-8 bytes. Everyone else counts code
 * points. The composer's character ring and the server's draft validation both
 * call this, so they cannot disagree.
 */
export function providerTextLength(provider: string, text: string): number {
  if (provider === "x") {
    return weightedTweetLength(text);
  }
  if (provider === "bluesky") {
    return graphemeLength(text);
  }
  if (provider === "mastodon") {
    return mastodonWeightedLength(text);
  }
  if (provider === "youtube") {
    return utf8ByteLength(text);
  }
  return Array.from(text).length;
}
